/**
 * Times on the AutoCount and Data sources pages. The API names its instants `...Utc`, so a value
 * that arrives without an offset is read as UTC, not as the browser's local time.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Milliseconds since the epoch, or null for nothing or nonsense. */
export function parseUtc(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  let text = String(value).trim();
  // "2026-10-03T03:12:45.123" carries no zone: the field says it is UTC.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(text)) text += "Z";
  const ms = Date.parse(text);
  return Number.isNaN(ms) ? null : ms;
}

/** "3 s ago", "1 minute ago", "yesterday", "on 3 Oct 2026"; "" when there is no time. */
export function timeAgo(value, now) {
  const at = parseUtc(value);
  if (at == null) return "";
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 1) return "just now";
  if (seconds < 60) return seconds + " s ago";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes === 1 ? "1 minute ago" : minutes + " minutes ago";
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : hours + " hours ago";
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return days + " days ago";
  const date = new Date(at);
  return "on " + date.getDate() + " " + MONTHS[date.getMonth()] + " " + date.getFullYear();
}

/** What is left before `value` as "m:ss", or "" once it has passed. */
export function countdown(value, now) {
  const at = parseUtc(value);
  if (at == null) return "";
  const seconds = Math.ceil((at - now) / 1000);
  if (seconds <= 0) return "";
  return Math.floor(seconds / 60) + ":" + String(seconds % 60).padStart(2, "0");
}

/** "14:05", the local clock time of `value`; "" when there is no time. */
export function clockTime(value) {
  const at = parseUtc(value);
  if (at == null) return "";
  const date = new Date(at);
  return String(date.getHours()).padStart(2, "0") + ":" + String(date.getMinutes()).padStart(2, "0");
}

/** True when `value` lies no more than `ms` before now (a moment in the future counts too). */
export function seenWithin(value, now, ms) {
  const at = parseUtc(value);
  return at != null && now - at <= ms;
}

/** "22 Oct 2026", the local date of `value`; "" when there is no time. */
export function shortDate(value) {
  const at = parseUtc(value);
  if (at == null) return "";
  const date = new Date(at);
  return date.getDate() + " " + MONTHS[date.getMonth()] + " " + date.getFullYear();
}

export default { parseUtc, timeAgo, countdown, clockTime, seenWithin, shortDate };
